import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./BecomeAdvisorForm.scss";
import Modal from "../../common/components/Modal.jsx";
import {
  validateBySchema,
  becomeAdvisorSchema,
  validateFieldBySchema,
} from "../../common/utils/validation";
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from "../../constants";
import FormSelect from "../../common/components/FormSelect";
import invoke from "../../common/utils/invoke";
import IndiaFlag from "../../assets/images/india-flag.png";
import BrandLoader from "../../common/components/BrandLoader.jsx";

const initialFormState = {
  name: "",
  mobile: "",
  email: "",
  pincode: "",
  state: "",
  city: "",
};

const BecomeAdvisorForm = () => {
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [apiErrorMsg, setApiErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const [allPincodeData, setAllPincodeData] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [pincodeLoading, setPincodeLoading] = useState(false);

  /* ----------------------------------------
   * Load pincode master data on mount
   * --------------------------------------*/
  useEffect(() => {
    const loadData = async () => {
      setDataLoading(true);
      try {
        const data = await invoke.fetchAllBecomeAnAdvisor();
        setAllPincodeData(Array.isArray(data?.items) ? data.items : []);
      } catch {
        setAllPincodeData([]);
      } finally {
        setDataLoading(false);
      }
    };

    loadData();
  }, []);

  /* ----------------------------------------
   * Memoized States & Cities
   * --------------------------------------*/
  const states = useMemo(() => {
    return [
      ...new Set(allPincodeData.map((item) => item.state).filter(Boolean)),
    ].sort();
  }, [allPincodeData]);

  const citiesByState = useMemo(() => {
    const map = {};
    allPincodeData.forEach((item) => {
      if (!item.state || !item.city) return;
      if (!map[item.state]) map[item.state] = new Set();
      map[item.state].add(item.city);
    });
    return map;
  }, [allPincodeData]);

  const cities = useMemo(() => {
    if (!form.state) return [];
    return [...(citiesByState[form.state] || [])].sort();
  }, [form.state, citiesByState]);

  /* ----------------------------------------
   * Fetch state/city by pincode
   * --------------------------------------*/
  const fetchPincodeData = useCallback(
    (pin) => {
      if (!pin || pin.length !== 6) return;

      setPincodeLoading(true);

      const match = allPincodeData.find((item) => String(item.pinCode) === pin);

      if (match) {
        setForm((prev) => ({
          ...prev,
          state: match.state || "",
          city: match.city || "",
        }));
        setErrors((prev) => ({ ...prev, state: null, city: null }));
      } else {
        setForm((prev) => ({ ...prev, state: "", city: "" }));
      }

      setPincodeLoading(false);
    },
    [allPincodeData]
  );

  /* ----------------------------------------
   * Watch pincode changes
   * --------------------------------------*/
  useEffect(() => {
    if (!form.pincode || form.pincode.length !== 6) return;

    const timer = setTimeout(() => {
      fetchPincodeData(form.pincode);
    }, 300);

    return () => clearTimeout(timer);
  }, [form.pincode, fetchPincodeData]);

  /* ----------------------------------------
   * Handlers
   * --------------------------------------*/
  const handleChange = (e) => {
    const { name, value } = e.target;
    let sanitizedValue = value;

    // 🔹 Sanitize name field: allow only letters & spaces
    if (name === "name") {
      sanitizedValue = value
        .replace(/[^a-zA-Z\s]/g, "") // remove special chars & numbers
        .replace(/\s{2,}/g, " ") // prevent multiple spaces
        .trimStart(); // avoid leading space
    }

    if (name === "mobile") {
      sanitizedValue = value
        .replace(/\D/g, "") // digits only
        .replace(/^0+/, ""); // remove leading zeros
    }

    if (name === "pincode") {
      sanitizedValue = value
        .replace(/\D/g, "") // digits only
        .replace(/^0+/, ""); // remove leading zeros
    }

    setForm((prev) => {
      const updatedForm = { ...prev, [name]: sanitizedValue };

      // Validate only the changed field
      const fieldError = validateFieldBySchema(
        name,
        sanitizedValue,
        becomeAdvisorSchema,
        updatedForm
      );

      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: fieldError,
      }));

      return updatedForm;
    });

    // Clear any prior API feedback once the user starts editing again
    if (showSuccessModal) setShowSuccessModal(false);
    if (showErrorModal) setShowErrorModal(false);
    if (apiErrorMsg) setApiErrorMsg("");
  };

  const handleStateChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      state: value,
      city: "",
      pincode: "",
    }));
    setErrors((prev) => ({ ...prev, state: null, city: null, pincode: null }));
  };

  const handleCityChange = (e) => {
    const value = e.target.value;
    const match = allPincodeData.find(
      (item) => item.state === form.state && item.city === value
    );

    setForm((prev) => ({
      ...prev,
      city: value,
      pincode: match ? String(match.pinCode) : "",
    }));
    setErrors((prev) => ({ ...prev, city: null, pincode: null }));
  };

  /* ----------------------------------------
   * Submit
   * --------------------------------------*/
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateBySchema(form, becomeAdvisorSchema);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setApiErrorMsg("");
    setShowErrorModal(false);

    try {
      const response = await invoke.submitBecomeAnAdvisor({
        name: form.name.trim(),
        mobileNumber: Number(`91${form.mobile}`),
        email: form.email.trim(),
        pinCode: form.pincode,
        state: form.state,
        city: form.city,
      });

      // `invoke.submitBecomeAnAdvisor` returns a response object on success, otherwise null
      if (response) {
        setShowSuccessModal(true);
        setForm(initialFormState);
      } else {
        throw new Error("Submission failed");
      }
    } catch (err) {
      setApiErrorMsg(
        err?.response?.data?.message || ERROR_MESSAGES.GENERIC_ERROR
      );
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  /* ----------------------------------------
   * Render
   * --------------------------------------*/
  return (
    <div className="lead-container">
      <h2>Dream Big. Earn Big.</h2>
      {loading && (
        <div className="loader-overlay">
          <BrandLoader color="#97262A" loading={loading} size={15} />
        </div>
      )}
      <form className="lead-form" onSubmit={handleSubmit} autocomplete="off">
        <div className="row">
          <div className="field">
            <label className="label-one">Full Name</label>
            <input
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              placeholder="e.g: Abhishek Kapoor"
              autocomplete="off"
            />
            {errors.name && <span className="error">{errors.name}</span>}
          </div>

          <div className="field">
            <label className="label-one">Mobile No.</label>
            <div className="flag-input">
              <div className="flag-box">
                <img className="country-flag" src={IndiaFlag} alt="India" />
                <span className="dial-code">+91</span>
              </div>
              <input
                name="mobile"
                type="text"
                value={form.mobile}
                onChange={handleChange}
                inputMode="numeric"
                maxLength={10}
                placeholder="e.g. 98762 72383"
                autocomplete="off"
              />
            </div>
            {errors.mobile && <span className="error">{errors.mobile}</span>}
          </div>

          <div className="field">
            <label className="label-one">Email Address</label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Deepak@indusindnipponlife.com"
              autocomplete="off"
            />
            {errors.email && <span className="error">{errors.email}</span>}
          </div>

          <div className="field">
            <label className="label-one">PIN Code</label>
            <div className="pin-input">
              <span className="pin-loc">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14.5 7H12.9769C12.8584 5.72007 12.296 4.52188 11.387 3.61296C10.4781 2.70404 9.27993 2.14163 8 2.02312V0.5C8 0.367392 7.94732 0.240215 7.85355 0.146447C7.75979 0.0526784 7.63261 0 7.5 0C7.36739 0 7.24021 0.0526784 7.14645 0.146447C7.05268 0.240215 7 0.367392 7 0.5V2.02312C5.72007 2.14163 4.52188 2.70404 3.61296 3.61296C2.70404 4.52188 2.14163 5.72007 2.02312 7H0.5C0.367392 7 0.240215 7.05268 0.146447 7.14645C0.0526784 7.24021 0 7.36739 0 7.5C0 7.63261 0.0526784 7.75979 0.146447 7.85355C0.240215 7.94732 0.367392 8 0.5 8H2.02312C2.14163 9.27993 2.70404 10.4781 3.61296 11.387C4.52188 12.296 5.72007 12.8584 7 12.9769V14.5C7 14.6326 7.05268 14.7598 7.14645 14.8536C7.24021 14.9473 7.36739 15 7.5 15C7.63261 15 7.75979 14.9473 7.85355 14.8536C7.94732 14.7598 8 14.6326 8 14.5V12.9769C9.27993 12.8584 10.4781 12.296 11.387 11.387C12.296 10.4781 12.8584 9.27993 12.9769 8H14.5C14.6326 8 14.7598 7.94732 14.8536 7.85355C14.9473 7.75979 15 7.63261 15 7.5C15 7.36739 14.9473 7.24021 14.8536 7.14645C14.7598 7.05268 14.6326 7 14.5 7ZM7.5 12C6.60998 12 5.73995 11.7361 4.99993 11.2416C4.25991 10.7471 3.68314 10.0443 3.34254 9.22208C3.00195 8.39981 2.91283 7.49501 3.08647 6.62209C3.2601 5.74918 3.68868 4.94736 4.31802 4.31802C4.94736 3.68868 5.74918 3.2601 6.62209 3.08647C7.49501 2.91283 8.39981 3.00195 9.22208 3.34254C10.0443 3.68314 10.7471 4.25991 11.2416 4.99993C11.7361 5.73995 12 6.60998 12 7.5C11.9987 8.69307 11.5241 9.83689 10.6805 10.6805C9.83689 11.5241 8.69307 11.9987 7.5 12ZM7.5 5C7.00555 5 6.5222 5.14662 6.11107 5.42133C5.69995 5.69603 5.37952 6.08648 5.1903 6.54329C5.00108 7.00011 4.95157 7.50277 5.04804 7.98773C5.1445 8.47268 5.3826 8.91814 5.73223 9.26777C6.08186 9.6174 6.52732 9.8555 7.01227 9.95196C7.49723 10.0484 7.99989 9.99892 8.45671 9.8097C8.91352 9.62048 9.30397 9.30005 9.57867 8.88893C9.85338 8.4778 10 7.99445 10 7.5C10 6.83696 9.73661 6.20107 9.26777 5.73223C8.79893 5.26339 8.16304 5 7.5 5ZM7.5 9C7.20333 9 6.91332 8.91203 6.66664 8.7472C6.41997 8.58238 6.22771 8.34811 6.11418 8.07403C6.00065 7.79994 5.97094 7.49834 6.02882 7.20736C6.0867 6.91639 6.22956 6.64912 6.43934 6.43934C6.64912 6.22956 6.91639 6.0867 7.20736 6.02882C7.49834 5.97094 7.79994 6.00065 8.07403 6.11418C8.34811 6.22771 8.58238 6.41997 8.7472 6.66664C8.91203 6.91332 9 7.20333 9 7.5C9 7.89782 8.84196 8.27936 8.56066 8.56066C8.27936 8.84196 7.89782 9 7.5 9Z"
                    fill="#AE272C"
                  />
                </svg>
              </span>
              <input
                name="pincode"
                type="number"
                value={form.pincode}
                onChange={handleChange}
                inputMode="numeric"
                maxLength={6}
                placeholder="e.g. 122001"
                autocomplete="off"
              />
            </div>
            {pincodeLoading && <span className="loading-text">Loading…</span>}
            {errors.pincode && <span className="error">{errors.pincode}</span>}
          </div>
        </div>

        <div className="row">
          <div className="field">
            <FormSelect
              name="state"
              label="State"
              value={form.state}
              onChange={handleStateChange}
              options={states}
              placeholder={dataLoading ? "Loading states..." : "Select State"}
              error={errors.state}
              disabled={dataLoading}
            />
          </div>

          <div className="field">
            <FormSelect
              name="city"
              label="City"
              value={form.city}
              onChange={handleCityChange}
              options={cities}
              placeholder={!form.state ? "Select state first" : "Select City"}
              error={errors.city}
              disabled={!form.state || dataLoading}
            />
          </div>
        </div>

        <button className="btn primary-button regular submit-btn">
          Continue
        </button>
      </form>

      {/* SUCCESS MODAL */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setForm(initialFormState);
          setErrors({});
          setApiErrorMsg("");
          setShowSuccessModal(false);
        }}
        title="You’re all set!"
        type="success"
        btnTitle="Done"
      >
        <p>{SUCCESS_MESSAGES.FORM_SUBMITTED}</p>
      </Modal>
      {/* ERROR MODAL */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => {
          setForm(initialFormState);
          setErrors({});
          setApiErrorMsg("");
          setShowErrorModal(false);
        }}
        title="Something went wrong!"
        type="error"
        btnTitle="Try Again"
      >
        <p>{apiErrorMsg}</p>
      </Modal>
    </div>
  );
};

export default BecomeAdvisorForm;
